// EmailSyncConfigModal.jsx
import { useState, useEffect, useRef } from 'react';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
	Switch,
	FormControlLabel,
	Typography,
	Divider,
	Box,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Paper,
	Alert,
	Stack,
	Tooltip,
	CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import { authedApiFetch } from '../../../utils/Api';
import { useTheme } from '@mui/material/styles';
import EmailDirectionDropdown from './EmailDirectionDropdown';
import MicrosoftFolderDropdown from './MicrosoftFolderDropdown';
import GmailLabelDropdown from './GmailLabelDropdown';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

import { useEmailSyncConfig } from '../hooks/useEmailSyncConfig';
import { useEmailRules } from '../hooks/useEmailRules';
import { formatDateTime } from '../../../utils/utils';

const EmailRuleSection = ({
	title,
	type,
	items,
	handleDelete,
	disabled,
	tenantLevel,
}) => {
	const lowerCased = title.toLowerCase();

	return (
		<Paper variant='outlined' sx={{ p: 2 }}>
			<Typography variant='subtitle2' gutterBottom>
				{title}
			</Typography>
			{items.length === 0 ? (
				<Typography variant='body2' color='text.secondary'>
					No {lowerCased}
				</Typography>
			) : (
				<List dense>
					{items.map((item, index) => (
						<ListItem
							key={index}
							secondaryAction={
								tenantLevel || item.user_id ? (
									<IconButton
										edge='end'
										aria-label='delete'
										onClick={() => handleDelete(type, item.id)}
										size='small'
										disabled={disabled}
									>
										<DeleteIcon />
									</IconButton>
								) : (
									<span>Tenant Rule</span>
								)
							}
						>
							<ListItemText primary={item.value} />
						</ListItem>
					))}
				</List>
			)}
		</Paper>
	);
};
let initialModalState = {};
let initialModalStateString = '';

const EmailSyncConfigModal = ({
	tenant,
	user,
	microsoftSyncStartMin,
	googleSyncStartMin,
	isMostRecentJobBlocking,
	open,
	onClose,
	showNotification,
	tenantLevel = false,
	availableProviders = [],
	onConfigChange,
}) => {
	const [inclusionEnabled, setInclusionEnabled] = useState(
		tenant.use_inclusion_email_rules
	);

	const [syncAttachments, setSyncAttachments] = useState(
		tenant.sync_attachments
	);

	// Tenant Level Setting - applies to all users within the tenant
	// User Level Setting - applies to single user
	const {
		fetchEmailSyncConfigurations,
		ensureMicrosoftConfigExists,
		fetchMicrosoftFolders,
		ensureGmailConfigExists,
		fetchGmailLabels,
	} = useEmailSyncConfig(
		tenantLevel,
		tenant,
		user,
		showNotification,
		onConfigChange
	);

	const { fetchEmailRules, fetchInitialEmailRules } = useEmailRules(
		tenantLevel,
		tenant,
		inclusionEnabled,
		open,
		showNotification
	);

	const mapEmailRuleToState = (emailRule) => {
		return {
			ruleType: emailRule.rule_type,
			criteriaType: emailRule.criteria_type,
			value: emailRule.value,
		};
	};

	const stringSort = (a, b) => {
		const nameA = a.toUpperCase();
		const nameB = b.toUpperCase();
		if (nameA < nameB) {
			return -1;
		}
		if (nameA > nameB) {
			return 1;
		}
		return 0;
	};

	const sortSimplifyStringifyModalState = (modalState) => {
		return JSON.stringify({
			gmail: modalState.gmail,
			microsoft: modalState.microsoft,
			includeEmailBody: modalState.includeEmailBody,
			syncAttachments: modalState.syncAttachments,
			emailDirection: modalState.emailDirection,
			inclusionEnabled: modalState.inclusionEnabled,
			domainRules: modalState.domainRules
				.map((rule) => mapEmailRuleToState(rule))
				.sort((a, b) => stringSort(a.value, b.value)),
			senderTypeRules: modalState.senderTypeRules
				.map((rule) => mapEmailRuleToState(rule))
				.sort((a, b) => stringSort(a.value, b.value)),
			specificEmailRules: modalState.specificEmailRules
				.map((rule) => mapEmailRuleToState(rule))
				.sort((a, b) => stringSort(a.value, b.value)),
			salesforceReportId: modalState.salesforceReportId,
		});
	};

	const hasModalStateChanged = () => {
		const gmailState = {
			enabled: gmailEnabled,
			syncStart: gmailSyncStart,
			label: '',
		};
		if (availableProviders.includes('google')) {
			gmailState.label = selectedGmailLabel || '';
		}
		const microsoftState = {
			enabled: microsoftEnabled,
			syncStart: microsoftSyncStart,
			folder: '',
		};
		if (availableProviders.includes('microsoft')) {
			microsoftState.folder = selectedMicrosoftFolder || '';
		}
		const newState = {
			gmail: gmailState,
			microsoft: microsoftState,
			includeEmailBody: emailBodySync,
			syncAttachments: syncAttachments,
			emailDirection: emailDirection,
			inclusionEnabled: inclusionEnabled,
			domainRules: domainRules,
			senderTypeRules: senderTypeRules,
			specificEmailRules: specificEmailRules,
			salesforceReportId: (reportId || '').trim(),
		};
		const newStateString = sortSimplifyStringifyModalState(newState);
		return (
			initialModalStateString != '' &&
			initialModalStateString !== newStateString
		);
	};

	// Provider sync configurations
	const [microsoftSyncStart, setMicrosoftSyncStart] = useState(null);
	const [microsoftSyncStartConfigValue, setMicrosoftSyncStartConfigValue] =
		useState(null);
	const [gmailSyncStart, setGmailSyncStart] = useState(null);
	const [gmailSyncStartConfigValue, setGmailSyncStartConfigValue] =
		useState(null);

	const [microsoftEnabled, setMicrosoftEnabled] = useState(false);
	const [selectedMicrosoftFolder, setSelectedMicrosoftFolder] = useState('');
	const [microsoftFolders, setMicrosoftFolders] = useState([]);
	const [microsoftFolderLoading, setMicrosoftFolderLoading] = useState(false);
	const [microsoftFolderError, setMicrosoftFolderError] = useState(null);

	const [gmailEnabled, setGmailEnabled] = useState(false);
	const [selectedGmailLabel, setSelectedGmailLabel] = useState('');
	const [gmailLabels, setGmailLabels] = useState([]);
	const [gmailLabelLoading, setGmailLabelLoading] = useState(false);
	const [gmailLabelError, setGmailLabelError] = useState(null);

	// Controlled form state for the modal
	const [criteriaType, setCriteriaType] = useState('domain');
	const [criteriaValue, setCriteriaValue] = useState('');

	// Email rules
	const [domainRules, setDomainRules] = useState([]);
	const [senderTypeRules, setSenderTypeRules] = useState([]);
	const [specificEmailRules, setSpecificEmailRules] = useState([]);

	const theme = useTheme();

	// 'Include Email Body in CRM Sync' Toggle
	const initialEmailBodySync = tenantLevel
		? tenant?.email_body_sync
		: tenant?.email_body_sync && user?.email_body_sync;

	const [emailBodySync, setEmailBodySync] = useState(initialEmailBodySync);

	// 'Email Ingestion Direction' Dropdown
	const [emailDirection, setEmailDirection] = useState(tenant?.email_direction);

	const handleRuleTypeChange = async (event) => {
		setConfigLoading(true);

		setInclusionEnabled(event.target.checked);

		const newEmailRules = Object.groupBy(
			await fetchInitialEmailRules(event.target.checked),
			(r) => r.criteria_type
		);
		setDomainRules(newEmailRules.domain || []);
		setSenderTypeRules(newEmailRules.sender_type || []);
		setSpecificEmailRules(newEmailRules.specific_email || []);

		setConfigLoading(false);
	};

	// 'Email Sync' Page - User Level Setting
	const handleMicrosoftAutoSyncChange = async (event) => {
		const newValue = event.target.checked;
		setMicrosoftEnabled(newValue);
		if (!newValue) {
			setMicrosoftSyncStart(null);
			setMicrosoftSyncStartConfigValue(null);
		}
	};

	// Email Sync Page - User Level Setting
	const handleGmailAutoSyncChange = async (event) => {
		const newValue = event.target.checked;
		setGmailEnabled(newValue);
		if (!newValue) {
			setGmailSyncStart(null);
			setGmailSyncStartConfigValue(null);
		}
	};

	// Tenant Admin (Tenant Level Setting) & Email Sync Page (User Level Setting)
	const handleAddEmailRule = async () => {
		if (criteriaType === 'domain') {
			setDomainRules([
				...domainRules,
				{
					rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
					value: criteriaValue,
					criteria_type: criteriaType,
					user_id: tenantLevel ? null : user.id,
				},
			]);
		}
		if (criteriaType === 'sender_type') {
			setSenderTypeRules([
				...senderTypeRules,
				{
					rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
					value: criteriaValue,
					criteria_type: criteriaType,
					user_id: tenantLevel ? null : user.id,
				},
			]);
		}
		if (criteriaType === 'specific_email') {
			setSpecificEmailRules([
				...specificEmailRules,
				{
					rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
					value: criteriaValue,
					criteria_type: criteriaType,
					user_id: tenantLevel ? null : user.id,
				},
			]);
		}

		setCriteriaValue(''); // Clear form on success
	};

	// Tenant Admin (Tenant Level Setting) & Email Sync Page (User Level Setting)
	const handleDeleteEmailRule = async (type, id) => {
		if (type === 'domain') {
			setDomainRules(domainRules.filter((rule) => rule.id !== id));
		}
		if (type === 'sender_type') {
			setSenderTypeRules(senderTypeRules.filter((rule) => rule.id !== id));
		}
		if (type === 'specific_email') {
			setSpecificEmailRules(
				specificEmailRules.filter((rule) => rule.id !== id)
			);
		}
	};

	// Handle Microsoft folder selection - just update state, don't save yet
	const handleMicrosoftFolderChange = (folderId) => {
		setSelectedMicrosoftFolder(folderId);
	};

	// Fetch Microsoft folders when Microsoft is enabled
	const handleFetchMicrosoftFolders = async (configId) => {
		setMicrosoftFolderLoading(true);
		setMicrosoftFolderError(null);
		try {
			const result = await fetchMicrosoftFolders(configId);
			if (result.success) {
				const fetchedFolders = result.data.folders || [];
				setMicrosoftFolders(fetchedFolders);
				return fetchedFolders;
			} else {
				setMicrosoftFolderError('Failed to load Microsoft folders');
				return null;
			}
		} catch (error) {
			setMicrosoftFolderError('Error loading Microsoft folders');
			return null;
		} finally {
			setMicrosoftFolderLoading(false);
		}
	};

	// Handle Gmail label selection - just update state, don't save yet
	const handleGmailLabelChange = (labelId) => {
		setSelectedGmailLabel(labelId);
	};

	// Fetch Gmail labels when Gmail is enabled
	const handleFetchGmailLabels = async (configId) => {
		setGmailLabelLoading(true);
		setGmailLabelError(null);
		try {
			const result = await fetchGmailLabels(configId);
			if (result.success) {
				const labels = result.data.labels || [];
				setGmailLabels(labels);
				return labels;
			} else {
				setGmailLabelError('Failed to load Gmail labels');
				return null;
			}
		} catch (error) {
			setGmailLabelError('Error loading Gmail labels');
			return null;
		} finally {
			setGmailLabelLoading(false);
		}
	};

	// Tenant Admin (Tenant Level Setting)
	const handleReportId = (updatedReportId) => {
		setReportId(updatedReportId);

		if (updatedReportId) {
			if (criteriaType == 'domain') {
				setCriteriaType('sender_type');
			}
			setDomainRules(initialModalState.domainRules);
		}
	};

	const microsoftInputRef = useRef(null);
	const originalMicrosoftFolderRef = useRef('');
	const gmailInputRef = useRef(null);
	const originalGmailLabelRef = useRef('');

	const handleClose = () => {
		if (
			initialModalState &&
			initialModalState.microsoft &&
			initialModalState.gmail &&
			![null, undefined].includes(initialModalState.inclusionEnabled) &&
			![null, undefined].includes(initialModalState.includeEmailBody) &&
			![null, undefined].includes(initialModalState.syncAttachments) &&
			![null, undefined].includes(initialModalState.emailDirection) &&
			hasModalStateChanged()
		) {
			setMicrosoftEnabled(initialModalState.microsoft.enabled);
			setMicrosoftSyncStart(initialModalState.microsoft.syncStart);

			setGmailEnabled(initialModalState.gmail.enabled);
			setGmailSyncStart(initialModalState.gmail.syncStart);

			setInclusionEnabled(initialModalState.inclusionEnabled);
			setSyncAttachments(initialModalState.syncAttachments);
			setEmailBodySync(initialModalState.includeEmailBody);
			setEmailDirection(initialModalState.emailDirection);

			setDomainRules(initialModalState.domainRules);
			setSenderTypeRules(initialModalState.senderTypeRules);
			setSpecificEmailRules(initialModalState.specificEmailRules);

			setReportId(initialModalState.salesforceReportId);
		}
		initialModalStateString = '';
		initialModalState = {};
		onClose();
	};

	const handleSave = async () => {
		const trimmedReportId = (reportId || '').trim();

		if (
			trimmedReportId &&
			emailSyncConfig &&
			emailSyncConfig.report_id &&
			emailSyncConfig.report_id !== trimmedReportId
		) {
			const okToUpdate = window.confirm(
				`Update Report ID from "${emailSyncConfig.report_id}" to "${trimmedReportId}"?\nThis will reset the current sync state.`
			);
			if (!okToUpdate) return;
		}

		if (
			!trimmedReportId &&
			emailSyncConfig &&
			emailSyncConfig.report_id &&
			emailSyncConfig.status === 'running'
		) {
			showNotification(
				'Cannot delete while sync is running. Please wait for it to finish.',
				'warning'
			);
			return;
		}
		if (
			!trimmedReportId &&
			emailSyncConfig &&
			emailSyncConfig.report_id &&
			emailSyncConfig.status !== 'running'
		) {
			const okToDelete = window.confirm(
				'Delete Dynamic Domain List configuration? This will disable the feature.'
			);
			if (!okToDelete) return;
		}

		const selectedLabelObj = gmailLabels.find(
			(l) => l.id === selectedGmailLabel
		);
		const gmailState = {
			enabled: gmailEnabled,
			sync_start_at: gmailSyncStart,
		};
		if (availableProviders.includes('google')) {
			gmailState.container_id = selectedGmailLabel || null;
			gmailState.container_name = selectedLabelObj?.name || null;
		}
		const selectedFolderObj = microsoftFolders.find(
			(f) => f.id === selectedMicrosoftFolder
		);
		let microsoftState = {
			enabled: microsoftEnabled,
			sync_start_at: microsoftSyncStart,
		};
		if (availableProviders.includes('microsoft')) {
			microsoftState.container_id = selectedMicrosoftFolder || null;
			microsoftState.container_name = selectedFolderObj?.name || null;
		}

		const newState = {
			gmail: gmailState,
			microsoft: microsoftState,
			include_email_body: emailBodySync,
			email_direction: emailDirection,
			inclusion_enabled: inclusionEnabled,
			sync_attachments: syncAttachments,
			domain_rules: domainRules,
			sender_type_rules: senderTypeRules,
			specific_email_rules: specificEmailRules,
			report_id: trimmedReportId,
		};

		if (hasModalStateChanged()) {
			const message = {
				successMessage: `Successfully updated!`,
				errorMessage: `Failed to update.`,
				errorLog:
					'Failed to update email sync config settings due to the error:',
			};
			try {
				await authedApiFetch({
					endpoint: `/tenants/${tenant.id}/email_sync_configuration_settings`,
					method: 'POST',
					payload: {
						tenant_level: tenantLevel,
						new_state: newState,
					},
				});

				tenant.use_inclusion_email_rules = inclusionEnabled;
				if (tenantLevel) {
					tenant.email_body_sync = emailBodySync;
					tenant.email_direction = emailDirection;
					tenant.sync_attachments = syncAttachments;
				} else {
					user.email_body_sync = emailBodySync;
				}
				setInclusionEnabled(inclusionEnabled);
				setSyncAttachments(syncAttachments);
				setEmailBodySync(emailBodySync);
				setEmailDirection(emailDirection);

				showNotification(message.successMessage, 'success');
			} catch (error) {
				handleClose();
				console.error(message.errorLog, error.message);
				showNotification(message.errorMessage, 'error');
			}
		}

		initialModalStateString = '';
		initialModalState = {};
		onClose();
	};

	// --- Tenant Level Setting - Dynamic Domain List (Report-driven sync) ---
	const [emailSyncConfig, setEmailSyncConfig] = useState(null); // null => not configured
	const [reportId, setReportId] = useState('');
	const [configLoading, setConfigLoading] = useState(false);
	const [runningEmailDomainSync, setRunningEmailDomainSync] = useState(false);
	const isRunning =
		emailSyncConfig?.status === 'running' || isMostRecentJobBlocking;
	const hasConfig = !!emailSyncConfig;
	const shouldDisableGmailDatePicker =
		gmailEnabled && gmailSyncStartConfigValue;
	const shouldDisableMicrosoftDatePicker =
		microsoftEnabled && microsoftSyncStartConfigValue;

	const fetchEmailDomainSyncConfig = async () => {
		let cfg;
		try {
			setConfigLoading(true);
			cfg = await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/email_domain_sync`,
				method: 'GET',
			});
			setEmailSyncConfig(cfg);
			setReportId(cfg.report_id || '');
		} catch (e) {
			setEmailSyncConfig(null);
			setReportId('');
			return '';
		} finally {
			setConfigLoading(false);
		}
		return cfg;
	};

	// Fetch latest status from backend; returns updated config or null if 404
	const fetchEmailDomainSyncStatus = async () => {
		try {
			const cfg = await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/email_domain_sync`,
				method: 'GET',
			});
			setEmailSyncConfig(cfg);
			return cfg;
		} catch (e) {
			if (e?.status === 404) {
				setEmailSyncConfig(null);
			}
			return null;
		}
	};

	useEffect(() => {
		if (open && tenant?.id) {
			const fetchData = async () => {
				const fetchedEmailDomainSyncConfig = await fetchEmailDomainSyncConfig();
				const fetchedReportId = fetchedEmailDomainSyncConfig?.report_id || '';
				const initalEmailRules = Object.groupBy(
					await fetchInitialEmailRules(inclusionEnabled),
					(r) => r.criteria_type
				);

				const initalDomainRules = initalEmailRules.domain || [];
				const initalSenderlRules = initalEmailRules.sender_type || [];
				const initalSpecificEmailRules = initalEmailRules.specific_email || [];

				const shouldIncludeEmailBody = tenantLevel
					? tenant?.email_body_sync
					: tenant?.email_body_sync && user?.email_body_sync;

				setDomainRules(initalDomainRules);
				setSenderTypeRules(initalSenderlRules);
				setSpecificEmailRules(initalSpecificEmailRules);

				setEmailBodySync(shouldIncludeEmailBody);
				setEmailDirection(tenant?.email_direction);
				setSyncAttachments(tenant?.sync_attachments);

				setRunningEmailDomainSync(
					fetchedEmailDomainSyncConfig &&
						fetchedEmailDomainSyncConfig.status === 'running'
				);

				let initialMicrosoftEnabled = false;
				let initialMicrosoftSyncStart = null;
				let initialMicrosoftFolder = '';
				let initialGoogleEnabled = false;
				let initialGoogleSyncStart = null;
				let initialGoogleLabel = '';

				if (!tenantLevel) {
					const result = await fetchEmailSyncConfigurations();
					if (result.success && result.data) {
						// Find microsoft and gmail configs
						let microsoftConfig = result.data.find(
							(c) => c.provider === 'microsoft'
						);
						let gmailConfig = result.data.find((c) => c.provider === 'google');

						// Ensure Microsoft config exists (create if needed)
						if (!microsoftConfig) {
							const ensureResult = await ensureMicrosoftConfigExists();
							if (ensureResult.success) {
								microsoftConfig = { id: ensureResult.configId };
							}
						}

						// Ensure Gmail config exists (create if needed)
						if (!gmailConfig) {
							const ensureResult = await ensureGmailConfigExists();
							if (ensureResult.success) {
								gmailConfig = { id: ensureResult.configId };
							}
						}

						// Set enabled states
						if (microsoftConfig) {
							setMicrosoftEnabled(microsoftConfig.enabled || false);
							initialMicrosoftEnabled = microsoftConfig.enabled || false;
							if (microsoftConfig.sync_start_at) {
								const parsedDate = dayjs(microsoftConfig.sync_start_at);
								if (parsedDate.isValid()) {
									setMicrosoftSyncStart(parsedDate);
									setMicrosoftSyncStartConfigValue(parsedDate);
									initialMicrosoftSyncStart = parsedDate;
								}
							}
							// Load selected folder if it exists
							if (microsoftConfig.source_container_id) {
								setSelectedMicrosoftFolder(microsoftConfig.source_container_id);
								initialMicrosoftFolder = microsoftConfig.source_container_id;
								originalMicrosoftFolderRef.current =
									microsoftConfig.source_container_id;
							}
							// Fetch available folders only if Microsoft provider is available
							if (availableProviders.includes('microsoft')) {
								await handleFetchMicrosoftFolders(microsoftConfig.id);
							}
						}
						if (gmailConfig) {
							setGmailEnabled(gmailConfig.enabled || false);
							initialGoogleEnabled = gmailConfig.enabled || false;
							if (gmailConfig.sync_start_at) {
								const parsedDate = dayjs(gmailConfig.sync_start_at);
								if (parsedDate.isValid()) {
									setGmailSyncStart(parsedDate);
									setGmailSyncStartConfigValue(parsedDate);
									initialGoogleSyncStart = parsedDate;
								}
							}
							// Load selected label if it exists
							if (gmailConfig.source_container_id) {
								setSelectedGmailLabel(gmailConfig.source_container_id);
								initialGoogleLabel = gmailConfig.source_container_id;
								originalGmailLabelRef.current = gmailConfig.source_container_id;
							}
							// Fetch available labels only if Google provider is available
							if (availableProviders.includes('google')) {
								await handleFetchGmailLabels(gmailConfig.id);
							}
						}
					}
				} else {
					setMicrosoftSyncStart(null);
					setGmailSyncStart(null);
					setMicrosoftSyncStartConfigValue(null);
					setGmailSyncStartConfigValue(null);
				}

				const initialMicrosoftState = {
					enabled: initialMicrosoftEnabled,
					syncStart: initialMicrosoftSyncStart,
					folder: '',
				};
				if (availableProviders.includes('microsoft')) {
					initialMicrosoftState.folder = initialMicrosoftFolder || '';
				}

				const initialGmailState = {
					enabled: initialGoogleEnabled,
					syncStart: initialGoogleSyncStart,
					label: '',
				};
				if (availableProviders.includes('google')) {
					initialGmailState.label = initialGoogleLabel || '';
				}

				initialModalState = {
					gmail: initialGmailState,
					microsoft: initialMicrosoftState,
					includeEmailBody: shouldIncludeEmailBody,
					syncAttachments: syncAttachments,
					emailDirection: emailDirection,
					inclusionEnabled: tenant.use_inclusion_email_rules,
					domainRules: initalDomainRules,
					senderTypeRules: initalSenderlRules,
					specificEmailRules: initalSpecificEmailRules,
					salesforceReportId: (fetchedReportId || '').trim(),
				};

				initialModalStateString =
					sortSimplifyStringifyModalState(initialModalState);
			};
			setConfigLoading(true);
			fetchData();

			setConfigLoading(false);
		}
	}, [open, tenant?.id]);

	// Poll while the job is running
	useEffect(() => {
		if (!open || !tenant?.id) return;
		if (emailSyncConfig?.status !== 'running') return;

		const interval = setInterval(async () => {
			const cfg = await fetchEmailDomainSyncStatus();
			if (!cfg || cfg.status !== 'running') {
				clearInterval(interval);
				// On success, refresh rules so Domain Rules reflect new values
				if (cfg && cfg.status === 'succeeded') {
					fetchEmailRules();
				}
			} else if (cfg && cfg.status === 'running') {
				setRunningEmailDomainSync(true);
			}
		}, 2000);

		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, tenant?.id, emailSyncConfig?.status]);

	// If a config exists, do not allow selecting 'domain' for manual rule creation
	useEffect(() => {
		if (hasConfig && criteriaType === 'domain') {
			setCriteriaType('sender_type');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasConfig]);

	useEffect(() => {
		if (!open) {
			handleClose();
		}
	}, [open]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth='md'
			fullWidth
			scroll='paper'
		>
			<DialogTitle>Email Sync Configuration</DialogTitle>
			<DialogContent dividers>
				{hasModalStateChanged() && (
					<Alert
						severity='info'
						sx={{
							mb: 2,
							backgroundColor: theme.palette.background.lightBlue,
						}}
					>
						There are unsaved changes to your Email Sync Configuration, to save
						them, please press the Save button.
					</Alert>
				)}

				{!tenantLevel && (
					<>
						<Box sx={{ mb: 2 }}>
							<Typography
								variant='subtitle1'
								sx={{
									fontWeight: 600,
									fontSize: '0.9rem',
									textTransform: 'uppercase',
									letterSpacing: '0.5px',
								}}
								gutterBottom
							>
								Automatic Email Sync
							</Typography>

							<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
								Select which email providers to automatically sync every 5
								minutes.
							</Typography>

							<Stack spacing={1.5}>
								{/* Microsoft Provider */}
								{availableProviders.includes('microsoft') && (
									<Paper variant='outlined' sx={{ p: 1.5 }}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Tooltip
												title='Unable to edit or enable syncs while a sync is currently running.'
												placement='top'
												disableHoverListener={!isRunning}
												arrow
											>
												<FormControlLabel
													control={
														<Switch
															checked={microsoftEnabled}
															onChange={handleMicrosoftAutoSyncChange}
															color='primary'
															disabled={isRunning}
														/>
													}
													label=''
													sx={{ m: 0 }}
												/>
											</Tooltip>
											<Typography
												variant='body2'
												sx={{ fontWeight: 500, minWidth: 'fit-content' }}
											>
												Microsoft
											</Typography>
											{microsoftEnabled && (
												<Tooltip
													title="This is used by the next automatic job if it's after last synced job or no earlier than 7 days ago for the first job."
													placement='top'
													arrow
												>
													<Box sx={{ ml: 'auto', width: 240 }}>
														<DateTimePicker
															inputRef={microsoftInputRef}
															value={microsoftSyncStart}
															label='Start Sync From'
															slotProps={{
																textField: {
																	size: 'small',
																	fullWidth: true,
																	error: false,
																},
															}}
															onChange={(newValue) => {
																setMicrosoftSyncStart(newValue);
															}}
															timeSteps={{ minutes: 1 }}
															minDateTime={microsoftSyncStartMin}
															disableFuture
															disabled={
																isRunning || shouldDisableMicrosoftDatePicker
															}
															format='MM/DD/YYYY hh:mm A'
														/>
													</Box>
												</Tooltip>
											)}
										</Box>
									</Paper>
								)}

								{/* Gmail Provider */}
								{availableProviders.includes('google') && (
									<Paper variant='outlined' sx={{ p: 1.5 }}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Tooltip
												title='Unable to edit or enable syncs while a sync is currently running.'
												placement='top'
												disableHoverListener={!isRunning}
												arrow
											>
												<FormControlLabel
													control={
														<Switch
															checked={gmailEnabled}
															onChange={handleGmailAutoSyncChange}
															color='primary'
															disabled={isRunning}
														/>
													}
													label=''
													sx={{ m: 0 }}
												/>
											</Tooltip>
											<Typography
												variant='body2'
												sx={{ fontWeight: 500, minWidth: 'fit-content' }}
											>
												Gmail
											</Typography>
											{gmailEnabled && (
												<Tooltip
													title="This is used by the next automatic job if it's after last synced job or no earlier than 7 days ago for the first job."
													placement='top'
													arrow
												>
													<Box sx={{ ml: 'auto', width: 240 }}>
														<DateTimePicker
															inputRef={gmailInputRef}
															value={gmailSyncStart}
															label='Start Sync From'
															slotProps={{
																textField: {
																	size: 'small',
																	fullWidth: true,
																	error: false,
																},
															}}
															onChange={(newValue) => {
																setGmailSyncStart(newValue);
															}}
															timeSteps={{ minutes: 1 }}
															minDateTime={googleSyncStartMin}
															disableFuture
															disabled={
																isRunning || shouldDisableGmailDatePicker
															}
															format='MM/DD/YYYY hh:mm A'
														/>
													</Box>
												</Tooltip>
											)}
										</Box>
									</Paper>
								)}
							</Stack>
						</Box>
						<Divider sx={{ my: 2 }} />
					</>
				)}

				<Stack spacing={2}>
					<Typography
						variant='subtitle1'
						sx={{
							fontWeight: 600,
							fontSize: '0.9rem',
							textTransform: 'uppercase',
							letterSpacing: '0.5px',
						}}
						gutterBottom
					>
						Basic Settings
					</Typography>

					{/* 'Include Email Body in CRM Sync' toggle */}

					<Paper
						variant='outlined'
						sx={{ px: 3, py: 2, borderRadius: 0.8, backgroundColor: '#f9fbfc' }}
					>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'flex-start',
							}}
						>
							<Box sx={{ flex: 1, mr: 2 }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
									<Typography
										variant='subtitle1'
										sx={{ fontWeight: 600, mb: 0.1, fontSize: '0.9rem' }}
									>
										Include Email Body in CRM Sync
									</Typography>
									<Tooltip
										title={
											!tenantLevel && !tenant.email_body_sync
												? 'This setting is controlled by your tenant admin'
												: ''
										}
										placement='top'
										arrow
										slotProps={{
											tooltip: {
												sx: {
													borderRadius: '5px',
												},
											},
										}}
									>
										{!tenantLevel && !tenant.email_body_sync && (
											<InfoOutlined
												sx={{
													fontSize: '1rem',
													color: 'text.secondary',
													cursor: 'help',
													mb: 0.3,
												}}
											/>
										)}
									</Tooltip>
								</Box>

								<Typography
									variant='body2'
									color='text.secondary'
									sx={{ fontSize: '0.8rem' }}
								>
									Sync the full email content to your CRM records
								</Typography>
							</Box>
							<Switch
								data-testid='email-body-sync-switch'
								checked={emailBodySync}
								onChange={(e) => setEmailBodySync(e.target.checked)}
								disabled={!tenantLevel && !tenant.email_body_sync}
							/>
						</Box>
					</Paper>

					{/* 'Sync Email Attachments' toggle */}

					<Paper
						variant='outlined'
						sx={{ px: 3, py: 2, borderRadius: 0.8, backgroundColor: '#f9fbfc' }}
					>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'flex-start',
							}}
						>
							<Box sx={{ flex: 1, mr: 2 }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
									<Typography
										variant='subtitle1'
										sx={{ fontWeight: 600, mb: 0.1, fontSize: '0.9rem' }}
									>
										Include Email Attachments in CRM Sync
									</Typography>
									<Tooltip
										title={
											!tenantLevel
												? 'This setting is controlled by your tenant admin'
												: ''
										}
										placement='top'
										arrow
										slotProps={{
											tooltip: {
												sx: {
													borderRadius: '5px',
												},
											},
										}}
									>
										{!tenantLevel && (
											<InfoOutlined
												sx={{
													fontSize: '1rem',
													color: 'text.secondary',
													cursor: 'help',
													mb: 0.3,
												}}
											/>
										)}
									</Tooltip>
								</Box>

								<Typography
									variant='body2'
									color='text.secondary'
									sx={{ fontSize: '0.8rem' }}
								>
									Sync email attachments to your CRM records
								</Typography>
							</Box>
							<Switch
								checked={syncAttachments}
								onChange={(e) => setSyncAttachments(e.target.checked)}
								disabled={!tenantLevel}
							/>
						</Box>
					</Paper>

					{/* 'Email Ingestion Direction' Dropdown */}
					<EmailDirectionDropdown
						emailDirection={emailDirection}
						onEmailDirectionChange={setEmailDirection}
						tenantLevel={tenantLevel}
					/>

					{/* Microsoft Folder Selection - Independent of Auto Sync */}
					{availableProviders.includes('microsoft') && (
						<MicrosoftFolderDropdown
							folders={microsoftFolders}
							selectedFolder={selectedMicrosoftFolder}
							onFolderChange={handleMicrosoftFolderChange}
							isLoading={microsoftFolderLoading}
							error={microsoftFolderError}
							disabled={isRunning}
							emailDirection={emailDirection}
						/>
					)}

					{/* Gmail Label Selection - Independent of Auto Sync */}
					{availableProviders.includes('google') && (
						<GmailLabelDropdown
							labels={gmailLabels}
							selectedLabel={selectedGmailLabel}
							onLabelChange={handleGmailLabelChange}
							isLoading={gmailLabelLoading}
							error={gmailLabelError}
							disabled={isRunning}
							emailDirection={emailDirection}
						/>
					)}
				</Stack>

				<Divider sx={{ my: 3 }} />

				<Typography
					variant='subtitle1'
					sx={{
						fontWeight: 600,
						fontSize: '0.9rem',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}
					gutterBottom
				>
					Email Rules
				</Typography>

				<Box>
					<Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2 }}>
						<Typography variant='body2' color='text.secondary'>
							Current Rule Type:
						</Typography>
						<Typography sx={{ fontWeight: inclusionEnabled ? 400 : 600 }}>
							Exclusion
						</Typography>
						{tenantLevel ? (
							<Switch
								checked={inclusionEnabled}
								onChange={handleRuleTypeChange}
								disabled={isRunning}
							/>
						) : (
							<Tooltip
								title='This setting is controlled by your tenant admin'
								slotProps={{
									tooltip: {
										sx: {
											borderRadius: '5px',
										},
									},
								}}
							>
								<span>
									<Switch
										sx={{
											m: 0,
											'& .MuiSwitch-track': {
												backgroundColor: '#8C8EFF',
											},
										}}
										checked={inclusionEnabled}
										disabled
									/>
								</span>
							</Tooltip>
						)}
						<Typography sx={{ fontWeight: inclusionEnabled ? 600 : 400 }}>
							Inclusion
						</Typography>
					</Stack>
					<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
						Emails matching these rules will{' '}
						{inclusionEnabled ? 'be' : 'not be'} synced to Altvia.
					</Typography>
				</Box>

				{(tenantLevel || !inclusionEnabled) && (
					<>
						<Alert
							severity='info'
							sx={{
								mb: 2,
								backgroundColor: theme.palette.background.lightBlue,
							}}
						>
							Changes to email rules will be applied to new ingestion jobs, but
							not to existing ones.
						</Alert>

						<Box sx={{ display: 'flex', mb: 2, alignItems: 'flex-start' }}>
							<FormControl sx={{ minWidth: 200, mr: 2 }}>
								<InputLabel id='criteria-type-label'>Criteria Type</InputLabel>
								<Select
									labelId='criteria-type-label'
									value={criteriaType}
									label='Criteria Type'
									onChange={(e) => setCriteriaType(e.target.value)}
									disabled={isRunning}
								>
									<MenuItem value='domain' disabled={hasConfig || reportId}>
										Domain
									</MenuItem>
									<MenuItem value='sender_type'>Sender Type</MenuItem>
									<MenuItem value='specific_email'>Specific Email</MenuItem>
								</Select>
							</FormControl>

							<TextField
								label={
									criteriaType === 'domain'
										? 'Domain (e.g., example.com)'
										: criteriaType === 'sender_type'
											? 'Sender Type (e.g., no-reply)'
											: 'Email Address'
								}
								value={criteriaValue}
								onChange={(e) => setCriteriaValue(e.target.value)}
								sx={{ flexGrow: 1, mr: 1 }}
								disabled={isRunning || (inclusionEnabled && !tenantLevel)}
							/>

							<Button
								variant='contained'
								startIcon={<AddIcon />}
								onClick={handleAddEmailRule}
								disabled={isRunning || (!tenantLevel && inclusionEnabled)}
								sx={{ mt: 1 }}
							>
								Add
							</Button>
						</Box>
					</>
				)}

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<EmailRuleSection
						title='Domain Rules'
						type='domain'
						items={domainRules}
						handleDelete={handleDeleteEmailRule}
						disabled={
							isRunning || (inclusionEnabled && !tenantLevel) || hasConfig
						}
						tenantLevel={tenantLevel}
					/>

					<EmailRuleSection
						title='Sender Type Rules'
						type='sender_type'
						items={senderTypeRules}
						handleDelete={handleDeleteEmailRule}
						disabled={isRunning || (inclusionEnabled && !tenantLevel)}
						tenantLevel={tenantLevel}
					/>

					<EmailRuleSection
						title='Specific Email Rules'
						type='specific_email'
						items={specificEmailRules}
						handleDelete={handleDeleteEmailRule}
						disabled={isRunning || (inclusionEnabled && !tenantLevel)}
						tenantLevel={tenantLevel}
					/>
				</Box>

				<Divider sx={{ mt: 4, mb: 3 }} />

				<Typography
					variant='subtitle1'
					sx={{
						fontWeight: 600,
						fontSize: '0.9rem',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}
					gutterBottom
				>
					Dynamic Domain List
				</Typography>

				<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
					Input a Salesforce Report Id below to extract domains from email
					addresses associated to contacts records included within the report.
					The domain list will update nightly, and will disable the ability to
					add any domains manually.
				</Typography>

				<Tooltip
					title={
						!tenantLevel
							? 'This setting is controlled by your tenant admin'
							: ''
					}
					placement='top'
					arrow
					slotProps={{
						tooltip: {
							sx: {
								borderRadius: '5px',
							},
						},
					}}
				>
					<Stack direction='row' spacing={2} alignItems='flex-start'>
						<TextField
							label='Salesforce Report ID'
							value={reportId}
							onChange={(e) => handleReportId(e.target.value)}
							placeholder='e.g., 00OXXXXXXXXXXXX'
							sx={{
								flexGrow: 1,
								minWidth: 280,
								// Disable hover state for non-tenant level
								...(!tenantLevel && {
									'& .MuiOutlinedInput-root': {
										'& fieldset': {
											borderColor: 'rgba(0, 0, 0, 0.23)',
										},
										'&:hover .MuiOutlinedInput-notchedOutline': {
											borderColor: 'rgba(0, 0, 0, 0.23)',
										},
									},
								}),
							}}
							disabled={
								!tenantLevel ||
								configLoading ||
								runningEmailDomainSync ||
								emailSyncConfig?.status === 'running'
							}
						/>
					</Stack>
				</Tooltip>

				{emailSyncConfig && (
					<Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
						{emailSyncConfig.status === 'running' && (
							<CircularProgress size={12} thickness={5} />
						)}
						<Typography variant='caption' color='text.secondary'>
							Current status: {emailSyncConfig.status || 'pending'}
							{emailSyncConfig.last_run_at
								? ` · Last run: ${formatDateTime(emailSyncConfig.last_run_at)}`
								: ''}
							{emailSyncConfig.last_error
								? ` · Last error: ${emailSyncConfig.last_error}`
								: ''}
						</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} variant='contained'>
					Cancel
				</Button>
				<Button onClick={handleSave} variant='contained'>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EmailSyncConfigModal;
